import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Quotes, Trash, Eye, EyeSlash, PencilSimple, Plus, X } from "@phosphor-icons/react";

const EMPTY = { first_name: "", city: "", text: "", photo: null, date_label: "", active: true };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = no panel, "new" = creation, or testimonial_id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(data);
    } catch { toast.error("Erreur de chargement"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (t) => {
    setForm({
      first_name: t.first_name || "",
      city: t.city || "",
      text: t.text || "",
      photo: t.photo || null,
      date_label: t.date_label || "",
      active: !!t.active,
    });
    setEditing(t.testimonial_id);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const photo = await compressImage(file);
      setForm((f) => ({ ...f, photo }));
    } catch { toast.error("Impossible de lire l'image"); }
  };

  const save = async () => {
    if (!form.first_name.trim() || form.text.trim().length < 10) {
      toast.error("Prénom et témoignage (10+ caractères) requis"); return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        const { data } = await api.post("/admin/testimonials", form);
        setItems((xs) => [data, ...xs]);
        toast.success("Témoignage ajouté");
      } else {
        const { data } = await api.patch(`/admin/testimonials/${editing}`, form);
        setItems((xs) => xs.map((x) => (x.testimonial_id === editing ? data : x)));
        toast.success("Témoignage mis à jour");
      }
      close();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const toggleActive = async (t) => {
    try {
      const { data } = await api.patch(`/admin/testimonials/${t.testimonial_id}`, { active: !t.active });
      setItems((xs) => xs.map((x) => (x.testimonial_id === t.testimonial_id ? data : x)));
      toast.success(data.active ? "Affiché sur le site" : "Masqué");
    } catch { toast.error("Erreur"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer le témoignage de ${t.first_name} ?`)) return;
    try {
      await api.delete(`/admin/testimonials/${t.testimonial_id}`);
      setItems((xs) => xs.filter((x) => x.testimonial_id !== t.testimonial_id));
      toast.success("Supprimé");
    } catch { toast.error("Erreur"); }
  };

  const copyRequestMailto = () => {
    const subject = encodeURIComponent("Témoignage Doudou Alert ✨");
    const body = encodeURIComponent(
      "Bonjour Carole,\n\nVoici notre histoire avec Doudou Alert :\n\n[Racontez ici ce qui s'est passé : la perte, la découverte, vos émotions...]\n\nPrénom de l'enfant / parent : \nVille (facultatif) : \nDate des retrouvailles : \n\nVous avez mon accord pour publier ce témoignage sur le site.\n\nMerci pour ce service !"
    );
    const link = `mailto:contact@doudou-alert.fr?subject=${subject}&body=${body}`;
    navigator.clipboard.writeText(link);
    toast.success("Lien mailto: copié — collez-le dans vos posts sociaux");
  };

  return (
    <div className="space-y-4" data-testid="admin-testimonials-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-display text-xl font-black text-stone-800 flex items-center gap-2">
            <Quotes size={22} weight="duotone" className="text-orange-500" /> Témoignages publiés
          </p>
          <p className="text-sm text-stone-500">Saisissez les histoires reçues par email, activez/désactivez l'affichage.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={copyRequestMailto} variant="outline" className="rounded-full font-bold" data-testid="admin-testimonial-copy-mailto">
            Copier lien "Envoyer témoignage"
          </Button>
          <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 rounded-full font-bold" data-testid="admin-testimonial-new-button">
            <Plus size={18} className="mr-1" weight="bold" /> Nouveau témoignage
          </Button>
        </div>
      </div>

      {editing && (
        <div className="bg-white rounded-3xl border-2 border-orange-200 p-6 space-y-3" data-testid="admin-testimonial-form">
          <div className="flex items-center justify-between">
            <p className="font-display font-black text-stone-800">{editing === "new" ? "Nouveau témoignage" : "Modifier le témoignage"}</p>
            <button onClick={close} className="text-stone-400 hover:text-stone-700" data-testid="admin-testimonial-close-form"><X size={20} /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>Prénom *</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Sophie" className="rounded-2xl mt-1" data-testid="admin-testimonial-firstname" />
            </div>
            <div>
              <Label>Ville</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Toulouse" className="rounded-2xl mt-1" data-testid="admin-testimonial-city" />
            </div>
            <div>
              <Label>Date / période</Label>
              <Input value={form.date_label} onChange={(e) => setForm({ ...form, date_label: e.target.value })} placeholder="Mai 2025" className="rounded-2xl mt-1" data-testid="admin-testimonial-date" />
            </div>
          </div>
          <div>
            <Label>Témoignage *</Label>
            <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={5} placeholder="Grâce à Doudou Alert..." className="rounded-2xl mt-1" data-testid="admin-testimonial-text" />
            <p className="text-xs text-stone-400 mt-1">{form.text.length} caractères (10 min — 1200 max)</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="cursor-pointer">
              <input type="file" accept="image/*" onChange={onPhotoChange} className="hidden" data-testid="admin-testimonial-photo-input" />
              <span className="px-4 py-2 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-sm">{form.photo ? "Changer la photo" : "Ajouter une photo (facultatif)"}</span>
            </label>
            {form.photo && (
              <div className="relative">
                <img src={form.photo} alt="aperçu" className="w-16 h-16 rounded-2xl object-cover" />
                <button onClick={() => setForm({ ...form, photo: null })} className="absolute -top-1 -right-1 w-6 h-6 bg-white border border-rose-200 rounded-full grid place-items-center" data-testid="admin-testimonial-photo-remove"><X size={12} /></button>
              </div>
            )}
            <label className="ml-auto flex items-center gap-2 text-sm font-bold">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="admin-testimonial-active-toggle" />
              Afficher sur le site
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={close} className="rounded-full font-bold">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 rounded-full font-bold" data-testid="admin-testimonial-save">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-stone-500">Chargement...</p>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-3xl border border-rose-100 p-10 text-center">
          <Quotes size={56} weight="duotone" className="mx-auto text-rose-300" />
          <p className="mt-4 text-stone-500">Aucun témoignage pour le moment.</p>
          <p className="text-xs text-stone-400 mt-1">Cliquez sur "Nouveau témoignage" pour saisir une histoire reçue par email.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((t) => (
            <div key={t.testimonial_id} className={`bg-white rounded-2xl border p-4 ${t.active ? "border-rose-100" : "border-stone-200 opacity-60"}`} data-testid={`admin-testimonial-row-${t.testimonial_id}`}>
              <div className="flex gap-3">
                {t.photo && <img src={t.photo} alt={t.first_name} className="w-14 h-14 rounded-2xl object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-bold text-stone-800">{t.first_name}{t.city ? <span className="text-stone-500 font-normal"> · {t.city}</span> : null}</p>
                    {t.date_label && <span className="text-xs text-stone-400">{t.date_label}</span>}
                    {!t.active && <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 text-xs font-bold">MASQUÉ</span>}
                  </div>
                  <p className="text-sm text-stone-700 mt-1 line-clamp-3 whitespace-pre-wrap">{t.text}</p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => toggleActive(t)} title={t.active ? "Masquer" : "Afficher"} className="p-2 rounded-full hover:bg-rose-50" data-testid={`admin-testimonial-toggle-${t.testimonial_id}`}>
                    {t.active ? <Eye size={18} className="text-emerald-600" /> : <EyeSlash size={18} className="text-stone-400" />}
                  </button>
                  <button onClick={() => openEdit(t)} className="p-2 rounded-full hover:bg-rose-50" data-testid={`admin-testimonial-edit-${t.testimonial_id}`}>
                    <PencilSimple size={18} className="text-stone-600" />
                  </button>
                  <button onClick={() => remove(t)} className="p-2 rounded-full hover:bg-rose-50" data-testid={`admin-testimonial-delete-${t.testimonial_id}`}>
                    <Trash size={18} className="text-rose-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
